import { useSessionStorage } from "@vueuse/core";
import type { FormData, SurveyData } from "~/types";
import {
  purposeList,
  workList,
  wishList,
  inflowSources,
} from "~/constants/form";

// 問い合わせフォーム　メインデーターの初期値
const initialInquiryData = {
  purpose: 2,
  works: 1,
  fullname: "",
  furigana: "",
  post: "",
  // お住まいの住所
  address: "",
  address_number: "",
  // お住まいと違う住所
  is_different_address: false,
  tel: "",
  email: "",
  // 施工例番号
  sekou_cd: 0,
  // レビュー番号
  kouji_cd: 0,
  // プラン番号
  plan_cd: 0,
  referer: "",
  // 任意項目
  message: "",

  // 工事先住所
  post2: "",
  address2: "",
  // 番地
  address2_number: "",
  completion: "",
  housemaker: "",
};

// 問い合わせフォーム　アンケートの初期値
const initialSurveyData = {
  q1: [],
  q2: 0,
};

const _useInquiryData = () =>
  useState<FormData>("inquiryData", () => ({ ...initialInquiryData }));

const _useSurvayData = () =>
  useState<SurveyData>("surveyData", () => ({ ...initialSurveyData }));

const _useAgreement = () => useState<boolean>("isAgree", () => false);

export default function () {
  // 問い合わせデータ
  const inquiryData = _useInquiryData();

  // 住所が工事場所とお住まい違うか同じか
  watch(
    () => inquiryData.value.is_different_address,
    (bool) => {
      if (bool) {
        //　住所と工事場所が違う場合
        inquiryData.value.post = "";
        inquiryData.value.address = "";
        inquiryData.value.address_number = "";
      }
    },
  );

  // 工事場所住所を監視し、お住まいの住所と同じ場合は値も同じにする
  watch(
    () => [
      inquiryData.value.post2,
      inquiryData.value.address2,
      inquiryData.value.address2_number,
    ],
    (val) => {
      if (false === inquiryData.value.is_different_address) {
        inquiryData.value.post = val[0] ? val[0] : "";
        inquiryData.value.address = val[1] ? val[1] : "";
        inquiryData.value.address_number = val[2] ? val[2] : "";
      }
    },
  );

  // 同意フラグ
  const isAgree = _useAgreement();

  // アンケートデータ
  const surveyData = _useSurvayData();

  // 問い合わせ選択済みデータ
  const selectedInquiry = computed(() => {
    const purpose = purposeList.find(
      (item) => item.id === inquiryData.value.purpose,
    );
    const works = workList.find((item) => item.id === inquiryData.value.works);

    const inq = { ...inquiryData.value };

    // 住所と番地を結合する
    inq.address += inq.address_number ? inq.address_number : "";
    inq.address2 += inq.address2_number ? inq.address2_number : "";

    return {
      ...inq,
      ...{ purpose, works },
    };
  });

  // Q1の選択済みデーター
  const selectedQ1 = computed(() => {
    return surveyData.value.q1.map((id) => {
      return wishList.find((item) => item.id === id);
    });
  });

  // Q2の選択済みデーター
  const selectedQ2 = computed(() => {
    let res = { id: 0, name: "" };
    const q2 = surveyData.value.q2;
    Object.values(inflowSources).forEach((group) => {
      const list = group.list.find((list) => list.id == q2);
      if (list) res = { id: list.id, name: list.name };
    });
    return res;
  });

  // サーバーへ送信する処理
  async function postData() {
    const data = await $fetch("/api/form/inquiry", {
      method: "POST",
      body: {
        inquiry: selectedInquiry.value,
        q1: selectedQ1.value,
        q2: selectedQ2.value,
      },
    });
    if (data.status == "ok") {
      // 送信処理成功後　データーリセット
      resetData();
      return true;
    }
    throw new Error();
  }

  // 問い合わせフォームの初期化
  function resetData() {
    inquiryData.value = { ...initialInquiryData };
    surveyData.value = { ...initialSurveyData };
    isAgree.value = false;

    // sessionStorageのデータを削除
    console.log("復元データをリセット");
    const data1 = useSessionStorage("inquiry-temp", {});
    const data2 = useSessionStorage("survey-temp", {});
    const data3 = useSessionStorage("is-agree", {});

    data1.value = null;
    data2.value = null;
    data3.value = null;
  }
  // sessionStorageから回復
  function restoreData() {
    // formから来て値が残っている場合なにもしない

    if (isAgree.value) return true;

    // agreementを復元 無い場合falseがstorageに保存される
    const agreement = useSessionStorage("is-agree", false);

    // agreementがtrueの時だけ復元する
    if (agreement.value === true) {
      isAgree.value = agreement.value;
      console.log("復元を開始");
      restoreInquiry();
      restoreSurvey();
      console.log("復元完了");
      return true;
    } else {
      // Storageの値を削除
      agreement.value = null;
      console.log("復元を中止");
      return false;
    }
  }
  // 問い合わせ内容の復元
  function restoreInquiry() {
    const form_temp = useSessionStorage("inquiry-temp", initialInquiryData, {
      mergeDefaults: true,
    });
    inquiryData.value = { ...form_temp.value };
  }

  // アンケート内容の復元
  function restoreSurvey() {
    const form_temp = useSessionStorage("survey-temp", initialSurveyData, {
      mergeDefaults: true,
    });
    surveyData.value = { ...form_temp.value };
  }

  return {
    isAgree,
    postData,
    resetData,
    restoreData,
    inquiryData,
    surveyData,
    selectedInquiry,
    selectedQ1,
    selectedQ2,
  };
}
